/* component-video-player.js
 * Slice 13 Phase A — shared host-agnostic video player engine.
 *
 * Click-to-play only. The engine never autoplays in Phase A. On user
 * activation it parses the sibling sources JSON, builds a <video>, and
 * appends it. Audible by design — click is intentional user gesture, no
 * autoplay-policy issue. Phase B will add an IntersectionObserver-gated
 * autoplay path (muted, viewport-near, page-visibility-aware) on a
 * separate opt-in flag, not in this file.
 *
 * The engine knows nothing about carousels, sections, blocks, or product
 * data — pure DOM contract. Multiple instances per page is supported; each
 * .video-player initialises independently (idempotent via dataset flag).
 *
 * Conventions match assets/component-carousel.js verbatim:
 *   - 'use strict'
 *   - var, not let/const
 *   - function declarations, not arrow functions
 *   - IIFE-dedup guard per Rule J12 (window.__BLOCKDEDUP_VIDEO_PLAYER__)
 *   - DOMContentLoaded + shopify:section:load for theme-editor hot reload
 *
 * Cleanup discipline (Rule J7): all listeners are attached to the player
 * container DOM. When Shopify section-reloads the container or any
 * ancestor is removed, the container plus its listeners GC together. No
 * page-level listeners are added.
 *
 * Phase D (Slice 13): activate(opts) accepts opts.sources (array of
 * {url, mime_type}) to override the init-captured sources. Used by the
 * fullscreen layer to re-source a single shared <dialog> container per
 * card open. Empty closure sources is now legal at init; activate() is
 * the gate for the usable-sources requirement.
 */
(function () {
  'use strict';

  if (window.__BLOCKDEDUP_VIDEO_PLAYER__) {
    return;
  }
  window.__BLOCKDEDUP_VIDEO_PLAYER__ = true;

  function initPlayer(containerEl) {
    if (containerEl.dataset.videoPlayerInit === 'true') {
      return;
    }
    containerEl.dataset.videoPlayerInit = 'true';

    var sourcesScript = containerEl.querySelector('.video-player__sources');
    if (!sourcesScript) {
      return;
    }

    /* Phase D hotfix #1: removed the .video-player__play init gate.
       The Phase D fullscreen container is event-activated (no click affordance
       inside it; engine listens for video-player:activate dispatched by the
       section JS). Original gate skipped init for containers without a play
       button, which silently broke the fullscreen path. playButton was only
       referenced here in the entire engine — safe removal. */

    var posterImg = containerEl.querySelector('.video-player__poster');

    var sources;
    try {
      sources = JSON.parse(sourcesScript.textContent || '[]');
    } catch (err) {
      return;
    }
    if (!Array.isArray(sources)) {
      return;
    }
    /* Phase D: sources may be empty at init when the host (the Phase D
       fullscreen snippet) renders an empty-sources [data-video-player]
       container that is re-sourced per-open via the activate event's
       detail.sources override. The downstream usable-sources gate now
       lives inside activate(). */

    function activate(opts) {
      if (containerEl.classList.contains('is-playing')) {
        return;
      }

      var isMuted = !!(opts && opts.muted);
      var showControls = !opts || opts.controls !== false;

      var videoEl = document.createElement('video');
      videoEl.setAttribute('playsinline', '');
      videoEl.preload = 'auto';
      videoEl.muted = isMuted;
      videoEl.controls = showControls;

      if (posterImg && posterImg.currentSrc) {
        videoEl.poster = posterImg.currentSrc;
      }

      /* Phase D: opts.sources override — callers (Slice 13 Phase D
         fullscreen layer) may pass an alternative source array to
         activate the player with a different set than was captured
         at init. Backward-compatible:
         - Phase A click handler calls activate() no-args ->
           opts undefined -> closure sources used.
         - Phase B.2 autoplay listener dispatches {muted, controls}
           only -> opts.sources undefined -> closure sources used. */
      var activeSources = (opts && Array.isArray(opts.sources) && opts.sources.length > 0)
        ? opts.sources
        : sources;
      if (activeSources.length === 0) {
        /* No usable sources from closure OR override. Bail silently;
           the container's listeners remain attached for a future
           :activate dispatch that may carry sources. */
        return;
      }

      var i;
      var src;
      var sourceEl;
      for (i = 0; i < activeSources.length; i++) {
        src = activeSources[i];
        if (!src || !src.url) {
          continue;
        }
        sourceEl = document.createElement('source');
        sourceEl.src = src.url;
        if (src.mime_type) {
          sourceEl.type = src.mime_type;
        }
        videoEl.appendChild(sourceEl);
      }

      containerEl.classList.add('is-playing');
      containerEl.appendChild(videoEl);

      var playPromise = videoEl.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          /* User-gesture or codec rejection. The <video> stays in the DOM
           * with controls visible so the user can hit native play. */
        });
      }
    }

    function teardown() {
      if (!containerEl.classList.contains('is-playing')) {
        return;
      }
      var videoEl = containerEl.querySelector('video');
      if (videoEl) {
        try { videoEl.pause(); } catch (err) { /* benign */ }
        while (videoEl.firstChild) {
          videoEl.removeChild(videoEl.firstChild);
        }
        videoEl.removeAttribute('src');
        try { videoEl.src = ''; } catch (err) { /* benign — older mobile Safari decoder release */ }
        try { videoEl.load(); } catch (err) { /* benign */ }
        videoEl.remove();
      }
      containerEl.classList.remove('is-playing');
    }

    containerEl.addEventListener('click', function (event) {
      if (containerEl.classList.contains('is-playing')) {
        return;
      }
      event.preventDefault();
      activate();
    });

    containerEl.addEventListener('video-player:activate', function (event) {
      activate(event.detail || {});
    });

    containerEl.addEventListener('video-player:teardown', function () {
      teardown();
    });
  }

  function initAll(scopeEl) {
    var root = scopeEl || document;
    var players = root.querySelectorAll('[data-video-player]');
    var i;
    for (i = 0; i < players.length; i++) {
      initPlayer(players[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) {
      initAll(event.target);
    }
  });
})();
